import {
  Button,
  HStack,
  InfoBox,
  Input,
  notify,
  Select,
  Stack,
  Switch,
  Text,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import {
  AutonomousAgent,
  AutonomousExecutionMode,
  autonomousAgentService,
  CreateAutonomousAgentRequest,
} from "services/autonomousAgentService";
import { WidgetConfig } from "services/widgetService";
import { getThemeColor } from "theme/colors";

import PromptEditor from "./PromptEditor";

interface AutonomousAgentFormProps {
  agent: AutonomousAgent | null; // null → create mode
  widgets: WidgetConfig[];
  onSaved: (agent: AutonomousAgent, created: boolean) => void;
  /** Fires as the user changes the Response mode select (before saving), so the
   *  surrounding page can update mode-dependent UI (e.g. the webhook recipe) live. */
  onExecutionModeChange?: (mode: AutonomousExecutionMode) => void;
}

// A widget with no server mapping can still run KB/general chat, but has no API
// tools — worth warning about, not blocking.
const widgetHasServer = (w?: WidgetConfig) =>
  !!w &&
  (!!w.serverId ||
    (w.serverIds?.length ?? 0) > 0 ||
    (w.serverMappings?.length ?? 0) > 0);

const AutonomousAgentForm = ({
  agent,
  widgets,
  onSaved,
  onExecutionModeChange,
}: AutonomousAgentFormProps) => {
  const { t } = useTranslation();
  const isEdit = !!agent;

  const [name, setName] = useState("");
  const [widgetId, setWidgetId] = useState("");
  const [executionMode, setExecutionMode] =
    useState<AutonomousExecutionMode>("Async");
  const [isActive, setIsActive] = useState(true);
  const [prompt, setPrompt] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the form whenever a different agent (or create mode) is selected.
  useEffect(() => {
    setName(agent?.name ?? "");
    setWidgetId(agent?.widgetId ?? "");
    setExecutionMode(agent?.executionMode ?? "Async");
    setIsActive(agent?.isActive ?? true);
    setPrompt(agent?.prompt ?? "");
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.id]);

  const selectedWidget = widgets.find((w) => w.id === widgetId);
  const showServerWarning = !!widgetId && !widgetHasServer(selectedWidget);

  const handleSave = async () => {
    const trimmedName = name.trim();
    const trimmedPrompt = prompt.trim();
    if (!trimmedName) {
      setError(t("app.autonomousAgents.validation.nameRequired"));
      return;
    }
    if (!widgetId) {
      setError(t("app.autonomousAgents.validation.widgetRequired"));
      return;
    }
    if (!trimmedPrompt) {
      setError(t("app.autonomousAgents.validation.promptRequired"));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (isEdit && agent) {
        const updated = await autonomousAgentService.update(agent.id, {
          name: trimmedName,
          widgetId,
          executionMode,
          isActive,
          prompt: trimmedPrompt,
        });
        notify({
          type: "success",
          title: t("app.autonomousAgents.notifications.updated"),
        });
        onSaved(updated, false);
      } else {
        const payload: CreateAutonomousAgentRequest = {
          name: trimmedName,
          widgetId,
          executionMode,
          prompt: trimmedPrompt,
        };
        const created = await autonomousAgentService.create(payload);
        notify({
          type: "success",
          title: t("app.autonomousAgents.notifications.created"),
        });
        onSaved(created, true);
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.error ??
          t("app.autonomousAgents.validation.saveFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <VStack align="stretch" gap={4}>
      {/* Name */}
      <Stack gap={1}>
        <Text fontSize="sm" fontWeight="semibold">
          {t("app.autonomousAgents.fields.name")}
        </Text>
        <Input
          placeholder={t("app.autonomousAgents.fields.namePlaceholder")}
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setName(e.target.value)
          }
        />
      </Stack>

      {/* Widget + execution mode */}
      <HStack gap={3} align="flex-start">
        <Stack flex="1" gap={1}>
          <Text fontSize="sm" fontWeight="semibold">
            {t("app.autonomousAgents.fields.widget")}
          </Text>
          <Select
            placeholder={t("app.autonomousAgents.fields.widgetPlaceholder")}
            value={widgetId ? [widgetId] : []}
            onValueChange={(e: { value: string[] }) =>
              setWidgetId(e.value[0] ?? "")
            }
            items={widgets.map((w) => ({
              value: w.id ?? "",
              label: w.name,
            }))}
          />
        </Stack>
        <Stack flex="1" gap={1}>
          <Text fontSize="sm" fontWeight="semibold">
            {t("app.autonomousAgents.fields.executionMode")}
          </Text>
          <Select
            value={[executionMode]}
            onValueChange={(e: { value: string[] }) => {
              const mode = (e.value[0] as AutonomousExecutionMode) || "Async";
              setExecutionMode(mode);
              onExecutionModeChange?.(mode);
            }}
            items={[
              {
                value: "Async",
                label: t("app.autonomousAgents.mode.async"),
              },
              {
                value: "Synchronous",
                label: t("app.autonomousAgents.mode.sync"),
              },
            ]}
          />
        </Stack>
      </HStack>

      {showServerWarning && (
        <InfoBox variant="warning">
          <Text fontSize="sm">
            {t("app.autonomousAgents.fields.noServerWarning")}
          </Text>
        </InfoBox>
      )}

      {/* Active toggle (edit only — new agents are active by default) */}
      {isEdit && (
        <HStack justify="space-between">
          <Stack gap={0}>
            <Text fontSize="sm" fontWeight="semibold">
              {t("app.autonomousAgents.fields.active")}
            </Text>
            <Text fontSize="xs" color={getThemeColor("text.secondary")}>
              {t("app.autonomousAgents.fields.activeHint")}
            </Text>
          </Stack>
          <Switch
            checked={isActive}
            onCheckedChange={(e: any) => setIsActive(e.checked)}
            colorPalette="green"
          />
        </HStack>
      )}

      {/* Prompt */}
      <Stack gap={1}>
        <Text fontSize="sm" fontWeight="semibold">
          {t("app.autonomousAgents.fields.prompt")}
        </Text>
        <Text fontSize="xs" color={getThemeColor("text.secondary")}>
          {t("app.autonomousAgents.fields.promptHint")}
        </Text>
        <PromptEditor
          value={prompt}
          onChange={setPrompt}
          placeholder={t("app.autonomousAgents.fields.promptPlaceholder")}
        />
      </Stack>

      {error && (
        <Text color="red.500" fontSize="sm">
          {error}
        </Text>
      )}

      <HStack justify="flex-end">
        <Button
          intent="primary"
          onClick={handleSave}
          loading={saving}
          disabled={saving}
        >
          {isEdit ? t("app.common.save") : t("app.autonomousAgents.create")}
        </Button>
      </HStack>
    </VStack>
  );
};

export default AutonomousAgentForm;
